// Core lib dependencies.
import React from 'react';
import PropTypes from 'prop-types';

// Component dependencies.
import CookieConsent from 'react-cookie-consent';
import PrivacyPolicy from './PrivacyPolicy.jsx';
import OverlayInterface from '../../common/Overlay/index.js';

// Misc dependencies.
import { camelCase, capitalize } from '../../../common/utils/string.js';
import Enums from '../../../common/config/enums.js';

/**
 * <pre>
 * PrivacyManagement React component.
 *
 * Display the coookie / privacy consent popup to the user. Set their choices in the privacy service.
 *
 * </pre>
 *
 * @memberof module:PrivacyManagement.Components
 * @namespace
 *
 * @return {PrivacyManagement.Components.PrivacyPopup}
 */
class PrivacyPopup extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);

    // Get the privacy service.
    this.privacyService = NGXD.getServiceContainer().getService('privacy');

    // Privacy configs
    const configService = NGXD.getServiceContainer().getService('config');
    this.cookieDefaults = configService.get('privacy.cookieSettings');
    this.policyURL = configService.get('privacy.policyURL');

    // Bind methods.
    this.handleSaveSelections = this.handleSaveSelections.bind(this);

    this._setAll = this._setAll.bind(this);
    this.handleSelectAll = this.handleSelectAll.bind(this);
    this.handleUnselectAll = this.handleUnselectAll.bind(this);

    const privacyConsentSettings = props.currentConsents;
    // Initial state.
    this.state = {
      ...privacyConsentSettings,
    };
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {module:PrivacyManagement.Components.PrivacyPopup}
   */
  render() {
    const popupDefaults = {
      buttonClasses:        'xButton xCTA',
      buttonWrapperClasses: 'xCell xCTAContainer',
      contentClasses:       'xCell xForm',
    };

    if (!this.props.isVisible) {
      return null;
    }

    const checkboxes = this._composeConsentOptions();
    return (
      // Wrapper added for aria screen readers
      <OverlayInterface.Components.Overlay
        classes="xPrivacy_cookiePopupWrapper"
        allowUserClose={false}
        hasDynamicContent={true}
        role="dialog"
      >
        <CookieConsent
          // Cookie settings
          containerClasses="xPrivacy_popup"
          cookieName={this.cookieDefaults.name}
          expires={this.cookieDefaults.expirationDays}
          // Popup settings
          visible={this.props.isVisible ? 'show' : 'hidden'}
          contentClasses={popupDefaults.contentClasses}
          location="unset"
          disableButtonStyles={true}
          disableStyles={true}
          // Save & Close Button - wrap in a span to pull in our macro button styles
          buttonText={<span>{NGX.lang.privacy.consentPopup.save}</span>}
          buttonClasses={popupDefaults.buttonClasses}
          buttonWrapperClasses={popupDefaults.buttonWrapperClasses}
          ariaAcceptLabel={NGX.lang.privacy.consentPopup.save}
          onAccept={this.handleSaveSelections}
        >
          <p className="xCopy info" tabIndex="-1">
            <span>{NGX.lang.privacy.consentPopup.cookieStatement} </span>
            <PrivacyPolicy
              parameterizedText={NGX.lang.privacy.consentPopup.policyStatement}
              linkUrl={this.policyURL}
              linkTitle={NGX.lang.privacy.privacyPolicy.linkTitle}
            />
          </p>
          <div className="xFieldContainer">
            <div className="xFieldGroup">
              <div className="xCheckboxSelectionControls">
                <a href="#" onClick={this.handleUnselectAll}>
                  {NGX.lang.privacy.consentPopup.unselectAll}
                </a>
                <a href="#" onClick={this.handleSelectAll}>
                  {NGX.lang.privacy.consentPopup.selectAll}
                </a>
              </div>
              {checkboxes}
            </div>
          </div>
        </CookieConsent>
      </OverlayInterface.Components.Overlay>
    );
  }

  _setAll(setting) {
    const userConsentChoices = Object.assign({}, this.state);
    Object.keys(userConsentChoices).forEach((consentType) => {
      if (consentType !== Enums.PRIVACY_TYPES.NECESSARY) {
        userConsentChoices[consentType] = setting;
      }
    });
    return userConsentChoices;
  }

  handleSelectAll() {
    const userConsentChoices = this._setAll(true);
    this.setState(userConsentChoices);
  }

  handleUnselectAll() {
    const userConsentChoices = this._setAll(false);
    this.setState(userConsentChoices);
  }

  handleSaveSelections() {
    this.privacyService.set(this.state);
    this.props.onSave();
  }

  /**
   * Compose the checkbox items for the consent popup with the user's current choices. The necessary checkbox should
   * be disabled since users may not disallow those.
   *
   * @private
   *
   * @param {Object} incomingCookieValues - Current cookie consent choices to display to user as checked or unchecked.
   * @returns {Array} of HTML elements
   */
  _composeConsentOptions() {
    return Object.keys(this.state).map((cookieType, idx) => {
      return (
        <div key={cookieType} className="xFieldCheckboxChoice">
          <label
            className="xCheckbox xCookieSetting"
            id={`xForm-checkbox_${cookieType}`}
            role="checkbox"
            aria-checked={this.state[cookieType]}
            tabIndex={0}
          >
            <input
              type="checkbox"
              name={cookieType}
              id={cookieType}
              checked={this.state[cookieType]}
              disabled={cookieType === Enums.PRIVACY_TYPES.NECESSARY}
              aria-label={`${NGX.lang.privacy.consentPopup.cookieTypeAllow.allow.replace(
                '{0}',
                NGX.lang.privacy.consentPopup.cookieTypeAllow[camelCase(cookieType)]
              )}`}
              onChange={(e) => {
                e.target.checked = !e.target.checked;
                this.setState({ [e.target.name]: !e.target.checked });
              }}
            />
            <div className="xFormLabel xFieldLabel">{`${
              NGX.lang.privacy.consentPopup.cookieTypeAllow[camelCase(cookieType)]
            }`}</div>
          </label>
        </div>
      );
    });
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {Boolean} isVisible Should we show the privacy consent popup
 * @property {Function} onAcceptAll Function to call when handling the 'accept all' button click
 * @property {Function} onSave Function to call when handling the 'confirm my choices' button click
 */
PrivacyPopup.propTypes = {
  isVisible:   PropTypes.bool,
  onAcceptAll: PropTypes.func,
  onSave:      PropTypes.func,
};

PrivacyPopup.defaultProps = {
  isVisible:   false,
  onAcceptAll: () => {},
  onSave:      () => {},
};

// Export the component as default.
export default PrivacyPopup;
